import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as XLSX from 'xlsx';
import { api } from '../lib/api';
import { keys } from '../lib/queries';
import { useToast } from './Toast';

type Mode = 'append' | 'replace';

function isExcelDateCell(cell: XLSX.CellObject): boolean {
  if (!cell) return false;
  if (cell.t === 'd' || cell.v instanceof Date) return true;
  if (typeof cell.v !== 'number') return false;
  if (!cell.z) return false;
  if (XLSX.SSF.is_date(cell.z)) return true;
  const zLower = String(cell.z).toLowerCase().replace(/\[[^\]]+\]/g, '');
  return /\b(yy|yyyy|dd?|mm?)\b|dd\.mm|\.yyyy|yyyy-mm|mm\/dd/.test(zLower);
}

function formatExcelCellValue(cell: XLSX.CellObject): string {
  if (!cell) return '';
  if (isExcelDateCell(cell)) {
    if (typeof cell.v === 'number') {
      const parsed = XLSX.SSF.parse_date_code(cell.v);
      if (parsed) {
        const dd = String(parsed.d).padStart(2, '0');
        const mm = String(parsed.m).padStart(2, '0');
        const yyyy = String(parsed.y);
        if (parsed.H || parsed.M || parsed.S) {
          const hh = String(parsed.H).padStart(2, '0');
          const min = String(parsed.M).padStart(2, '0');
          return `${dd}.${mm}.${yyyy} ${hh}:${min}`;
        }
        return `${dd}.${mm}.${yyyy}`;
      }
    } else if (cell.v instanceof Date) {
      const d = new Date(cell.v.getTime() + 12 * 3600 * 1000);
      const dd = String(d.getUTCDate()).padStart(2, '0');
      const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
      const yyyy = String(d.getUTCFullYear());
      return `${dd}.${mm}.${yyyy}`;
    }
  }

  let raw = '';
  if (cell.w !== undefined && cell.w !== null && cell.w !== '') {
    raw = String(cell.w);
  } else if (cell.v !== undefined && cell.v !== null) {
    raw = String(cell.v);
  }

  if (raw) {
    const isoMatch = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?$/.exec(raw.trim());
    if (isoMatch) {
      const timePart = isoMatch[4] ? ` ${isoMatch[4]}:${isoMatch[5]}` : '';
      return `${isoMatch[3]}.${isoMatch[2]}.${isoMatch[1]}${timePart}`;
    }
    return raw;
  }
  return '';
}

export function ImportDialog({
  docId, tableId, onClose,
}: {
  docId: string;
  tableId: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);

  const [rows, setRows] = useState<unknown[][]>([]);
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [workbook, setWorkbook] = useState<XLSX.WorkBook | null>(null);
  const [hasHeader, setHasHeader] = useState(true);
  const [preserveDesign, setPreserveDesign] = useState(true);
  const [mode, setMode] = useState<Mode>('append');
  const [error, setError] = useState('');

  const readSheet = (wb: XLSX.WorkBook, name: string) => {
    const sheet = wb.Sheets[name];
    const data: string[][] = [];
    if (sheet['!ref']) {
      const range = XLSX.utils.decode_range(sheet['!ref']);
      for (let R = range.s.r; R <= range.e.r; ++R) {
        const row: string[] = [];
        let hasData = false;
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const cell = sheet[XLSX.utils.encode_cell({ r: R, c: C })];
          if (cell) {
            if (cell.f) {
              let inQuotes = false;
              let fixed = '';
              for (let i = 0; i < cell.f.length; i++) {
                if (cell.f[i] === '"') inQuotes = !inQuotes;
                if (cell.f[i] === ',' && !inQuotes) fixed += ';';
                else fixed += cell.f[i];
              }
              row.push('=' + fixed);
              hasData = true;
            } else {
              const val = formatExcelCellValue(cell);
              row.push(val);
              if (val.trim() !== '') hasData = true;
            }
          } else {
            row.push('');
          }
        }
        if (hasData) data.push(row);
      }
    }
    setRows(data);
  };

  const onFile = async (file: File) => {
    setError('');
    try {
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer, { cellNF: true, cellDates: false });
      setWorkbook(wb);
      setSheetNames(wb.SheetNames);
      readSheet(wb, wb.SheetNames[0]);
    } catch {
      setError('Не удалось прочитать файл. Поддерживаются xlsx, xls и csv.');
    }
  };

  const doImport = useMutation({
    mutationFn: () => api.importRows(docId, tableId, { rows, mode, hasHeader, preserveDesign }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: keys.records(docId, tableId) });
      qc.invalidateQueries({ queryKey: keys.columns(docId, tableId) });
      qc.invalidateQueries({ queryKey: ['styles', docId, tableId] });
      qc.invalidateQueries({ queryKey: ['merges', docId, tableId] });
      qc.invalidateQueries({ queryKey: ['sizes', docId, tableId] });
      qc.invalidateQueries({ queryKey: ['conditional', docId, tableId] });
      toast(`Импортировано строк: ${res.imported}`, 'ok');
      onClose();
    },
    onError: (e) => toast((e as Error).message, 'err'),
  });

  const preview = rows.slice(0, 6);
  const dataCount = hasHeader ? Math.max(0, rows.length - 1) : rows.length;

  return (
    <div className="rt-overlay" onMouseDown={onClose}>
      <div className="rt-dialog rt-scroll" onMouseDown={(e) => e.stopPropagation()}>
        <header className="rt-panel-head">
          <strong>Импорт из Excel</strong>
          <span className="rt-spacer" />
          <button className="rt-btn" onClick={onClose}>✕</button>
        </header>

        <div style={{ padding: 14, display: 'grid', gap: 12 }}>
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onFile(file);
            }}
          />

          <div
            className="rt-dropzone"
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const file = e.dataTransfer.files?.[0];
              if (file) onFile(file);
            }}
          >
            {rows.length
              ? `Прочитано строк: ${rows.length}`
              : 'Перетащите файл сюда или нажмите, чтобы выбрать'}
          </div>

          {error && <div className="rt-err">{error}</div>}

          {sheetNames.length > 1 && (
            <label style={{ display: 'grid', gap: 4 }}>
              <span className="rt-count">Лист</span>
              <select
                className="rt-input"
                onChange={(e) => workbook && readSheet(workbook, e.target.value)}
              >
                {sheetNames.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
          )}

          {rows.length > 0 && (
            <>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={hasHeader}
                  onChange={(e) => setHasHeader(e.target.checked)}
                />
                <span>Первая строка — заголовки</span>
              </label>

              <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer', background: 'var(--rt-panel)', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--rt-border)' }}>
                <input
                  type="checkbox"
                  checked={preserveDesign}
                  onChange={(e) => setPreserveDesign(e.target.checked)}
                  style={{ marginTop: 2 }}
                />
                <div style={{ display: 'grid', gap: 2 }}>
                  <span style={{ fontWeight: 600, fontSize: 13 }}>Сохранять оформление и дизайн таблицы</span>
                  <span className="rt-count" style={{ fontSize: 12 }}>
                    Ячейки не меняют цвет, шрифт, размер и объединения. Обновляются только значения и формулы.
                  </span>
                </div>
              </label>

              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                {(['append', 'replace'] as Mode[]).map((m) => (
                  <button
                    key={m}
                    className={mode === m ? 'rt-btn rt-btn--acc' : 'rt-btn'}
                    onClick={() => setMode(m)}
                  >
                    {m === 'append' ? 'Добавить к существующим' : 'Заменить все строки'}
                  </button>
                ))}
              </div>

              {mode === 'append' && (
                <div
                  className="rt-count"
                  style={{ fontSize: 12, padding: '5px 8px', borderRadius: 4, background: 'rgba(52, 152, 219, 0.12)', color: 'var(--rt-acc)' }}
                >
                  ✓ Умное объединение: существующие строки будут дополнены и обновлены данными из файла, а недостающие строки — добавлены.
                </div>
              )}

              {mode === 'replace' && (
                <div
                  className={preserveDesign ? 'rt-count' : 'rt-err'}
                  style={{ fontSize: 12, padding: '5px 8px', borderRadius: 4, background: preserveDesign ? 'rgba(46, 204, 113, 0.12)' : undefined }}
                >
                  {preserveDesign
                    ? '✓ Таблица обновится новыми данными, сохранив цвета шапок, шрифты и структуру ячеек.'
                    : '⚠ Внимание: все текущие строки и оформление таблицы будут удалены.'}
                </div>
              )}

              <div className="rt-gridwrap rt-scroll" style={{ maxHeight: 220 }}>
                <table className="rt-grid">
                  <tbody>
                    {preview.map((row, i) => (
                      <tr key={i}>
                        {row.map((cell, k) => (
                          <td
                            key={k}
                            style={
                              hasHeader && i === 0
                                ? { fontWeight: 600, background: 'var(--rt-panel)' }
                                : undefined
                            }
                          >
                            {String(cell ?? '')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="rt-count">
                Будет добавлено строк: {dataCount}. Колонки подбираются по
                названию заголовка, недостающие создаются автоматически.
              </div>
            </>
          )}

          <div style={{ display: 'flex', gap: 8 }}>
            <span className="rt-spacer" />
            <button className="rt-btn" onClick={onClose}>Отмена</button>
            <button
              className="rt-btn rt-btn--acc"
              disabled={!rows.length || doImport.isPending}
              onClick={() => doImport.mutate()}
            >
              {doImport.isPending ? 'Импортируем…' : 'Импортировать'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
